import { hasPermissions, UserPermissions } from '@order-management/roles';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Columns, Element } from 'react-bulma-components';
import {
  FaClock,
  FaFileCirclePlus,
  FaGears,
  FaMasksTheater,
} from 'react-icons/fa6';
import useUser from '../../shared/hooks/user';
import useUserHasZspaceInternalAndCustomerRoles from '../../shared/hooks/user-has-zspace-internal-and-customer-roles';
import If from '../../shared/if/if';
import Modal from '../../ui/modal/modal';
import UserAccessActionCard from '../admin-user-access-action-card/admin-user-access-action-card';
import styles from './admin-user-access-actions-modal.module.scss';

export type AdminUserAccessActionsModalProps = {
  show: boolean;
  onClose: () => void;
  onManageTemporaryAccess: () => void;
  onManageCustomerSalesOrders: () => void;
  onAddSalesOrderManagers: () => void;
  onManageInternalUsers: () => void;
};

export function AdminUserAccessActionsModal({
  show,
  onClose,
  onManageTemporaryAccess,
  onManageCustomerSalesOrders,
  onAddSalesOrderManagers,
  onManageInternalUsers,
}: AdminUserAccessActionsModalProps) {
  const { user } = useUser();
  const hasZspaceInternalAndCustomerRoles =
    useUserHasZspaceInternalAndCustomerRoles();
  const [showNoCustomerRoleError, setShowNoCustomerRoleError] =
    useState<boolean>(false);

  const handleManageCustomerSalesOrdersClick = useCallback(() => {
    if (hasZspaceInternalAndCustomerRoles) {
      onManageCustomerSalesOrders();
    } else {
      setShowNoCustomerRoleError(true);
    }
  }, [hasZspaceInternalAndCustomerRoles, onManageCustomerSalesOrders]);

  const canNavigateToTemporaryAccess = useMemo(
    () =>
      hasZspaceInternalAndCustomerRoles ||
      hasPermissions(user, [UserPermissions.USERS_SELF_ASSIGN_ROLES]),
    [hasZspaceInternalAndCustomerRoles, user]
  );

  const availableActions = useMemo(() => {
    const actionsData = [
      {
        title: 'Masquerade',
        subtitle:
          'Group devices and assign software as a customer. Requires Temporary Access.',
        icon: <FaMasksTheater className="w-full h-full" />,
        onClick: handleManageCustomerSalesOrdersClick,
        showError: showNoCustomerRoleError,
        errorMessage: 'You don’t have access to any customer’s orders.',
      },
      {
        title: 'Add Sales Order Managers',
        subtitle: 'Add managers to Sales Orders to support a customer.',
        icon: <FaFileCirclePlus className="w-full h-full" />,
        onClick: onAddSalesOrderManagers,
      },
      {
        title: 'Manage Internal Users',
        subtitle: 'Assign and edit roles for zSpace users.',
        icon: <FaGears className="w-full h-full" />,
        onClick: onManageInternalUsers,
      },
    ];

    if (canNavigateToTemporaryAccess) {
      return [
        {
          title: 'Manage Temporary Access',
          subtitle: 'Grant or revoke Temporary Access to Sales Orders.',
          icon: <FaClock className="w-full h-full" />,
          onClick: onManageTemporaryAccess,
        },
        ...actionsData,
      ];
    }
    return actionsData;
  }, [
    canNavigateToTemporaryAccess,
    handleManageCustomerSalesOrdersClick,
    onAddSalesOrderManagers,
    onManageInternalUsers,
    onManageTemporaryAccess,
    showNoCustomerRoleError,
  ]);

  const actionsRows = useMemo(() => {
    if (canNavigateToTemporaryAccess) {
      const firstRow = availableActions.slice(0, 2);
      const secondRow = availableActions.slice(2, 4);
      return [firstRow, secondRow];
    }
    return [availableActions];
  }, [availableActions, canNavigateToTemporaryAccess]);

  useEffect(() => {
    setShowNoCustomerRoleError(false);
  }, [show]);

  return (
    <Modal
      show={show}
      onClose={onClose}
      showClose
      closeOnBlur
      closeOnEsc
      cardClassName="w-fit-content"
      title="User access"
      titleClassName="has-text-primary pb-4"
    >
      <h2 className="is-size-6 has-text-weight-light pb-4">
        What do you want to do?
      </h2>
      {actionsRows.map((row, rowIndex) => (
        <Columns className="gap-6" marginless key={`actions-row-${rowIndex}`}>
          {row.map((action, actionIndex) => (
            <Columns.Column
              key={`actions-row-${rowIndex}-column-${actionIndex}`}
            >
              <UserAccessActionCard
                key={`action-card-${action.title}`}
                title={action.title}
                subtitle={action.subtitle}
                icon={action.icon}
                onClick={action.onClick}
              />
              <Element
                key={`action-card-${action.title}-error-container`}
                className={styles.errorMessageContainer}
              >
                <span
                  className="is-size-7 has-text-danger-dark"
                  key={`action-card-${action.title}-error-message`}
                >
                  <If condition={!!action.showError}>{action.errorMessage}</If>
                </span>
              </Element>
            </Columns.Column>
          ))}
        </Columns>
      ))}
    </Modal>
  );
}

export default AdminUserAccessActionsModal;
